import { OnboardingStatus } from '@common/onboarding/types';
import { BackEndCodeLanguage, ExternalGitProvider } from '@prophecy/graphql-types/enums';
import { getIsExpensiveCssDisabled } from '@prophecy/utils/expensiveCss';
import { BASE_PATH } from '@prophecy/utils/history';
import { useGlobalContext } from '@prophecy/utils/react/utilify';
import { parseJSON } from '@prophecy/utils/string';
import { useMemo } from 'react';

import {
  ALLOW_CONFIG_TAGS,
  ALLOW_DATA_PROFILE_ON_INTERIMS,
  DISABLE_ZSTD_COMPRESSION,
  ALLOW_FABRIC_ACCESS_CLUSTER_ADMIN,
  ALLOW_PARAM_CONFIG,
  ALLOW_SECRET_TYPE_CONFIG,
  CARRY_FORWARD_SLUG_ENABLED,
  COPILOT_AUDIO_PROMPT_ENABLED,
  COPILOT_ENABLED_KEY,
  DBT_MACRO_ENABLED_KEY,
  DBT_PROJECT_DEPENDENCY_ENABLED_KEY,
  DBT_SHOW_ALL_PROVIDERS,
  EDIT_DEFAULT_TEMPLATE_ENABLED_KEY,
  ENABLE_SPARK_SORT_FILTER,
  isDebugMode,
  PROJECT_BUILD_ENABLED_KEY,
  SPARK_PROPHECY_MANAGED_TIMEOUT,
  UNDO_REDO_KEY,
  ENABLE_OAUTH_FABRIC_TESTING,
  ENABLE_SAP,
  ENABLE_ORCHESTRATOR_DEBUG_MODE,
  ENABLE_GEMBUILDER_EDITOR,
  ENABLE_ANALYZER_FOR_INFORMATICA,
  ENABLE_PROFESSIONAL_EDITION,
  IS_REPO_ENABLED_IN_DEPENDENCY,
  ENABLE_TRANSPILER_IICS,
  ENABLE_SHARE_AND_REPLAYS_ENABLED,
  DISABLE_AUTO_SWITCH_IN_CHAT_MODE,
  ENABLE_DATA_HARMONIZATION,
  ENABLE_COPY_PASTE_IN_DIFFERENT_PROVIDERS,
  ENABLE_GOOGLE_OEM,
  ENABLE_HISTORICAL_RUN_SNAPSHOTS,
  ENABLE_SQL_LINEAGE
} from '../../utils/localstorage-keys';
import { FabricConnectionCreationRole } from '../constants';
import { UserDetails } from '../types';

export const FEATURE_MANAGEMENT_ENABLED_KEY = 'prophecy.ui.featureManagement.enabled';

export const useGlobalVariables = () => {
  const globalContext = useGlobalContext();
  const settings = useMemo(() => {
    try {
      // this is for dev purpose, ideally globalContext.globalConfig should be always available
      if (!globalContext.globalConfig) {
        throw new Error('Global config is not available');
      }
    } catch (error) {
      console.error(error);
    }
    return (globalContext.globalConfig || {}) as Record<string, unknown>;
  }, [globalContext.globalConfig]);

  return useMemo(
    () => ({
      getExternalGitPath: () => {
        const defaultPath = '/';
        const path = settings['prophecy.ui.externalgit.path'];
        if (!path) {
          return defaultPath;
        }
        return path;
      },
      get giteaUrl() {
        return settings['prophecy.ui.gitserver.url'] === '' ? undefined : settings['prophecy.ui.gitserver.url'];
      },
      get isAUTHBackendDatabase() {
        return settings['prophecy.ui.auth_backend'] === 'database';
      },
      get isCE() {
        return settings['prophecy.ui.IS_CE'] ?? true;
      },
      get isDarkCluster() {
        return (settings['prophecy.ui.darkcluster.enabled'] as boolean) ?? false;
      },
      get getOnboardingClusterId() {
        return settings['prophecy.ui.onboarding.db.clusterId'];
      },
      get isDeploymentEnabled() {
        return settings['prophecy.ui.cluster-screen.enabled'] ?? true;
      },
      get isExecutableCopyCodeEnabled() {
        return settings['prophecy.ui.workflow.code.copy.enabled'] ?? false;
      },
      get isOIDCEnabled() {
        return Boolean(settings['prophecy.ui.livy.oidc.enabled']);
      },
      get isFabricTrailEnabled() {
        return (settings['prophecy.ui.fabric.trial.enabled'] as boolean) ?? true;
      },
      get isCodeGenerationEnabled() {
        return settings['prophecy.ui.project.codeRegeneration.enabled'] ?? true;
      },
      get isPlibDependeciesEnabled() {
        return settings['prophecy.ui.editor.plibs-in-dependencies.enabled'] ?? false;
      },
      get isFabricCreationEnabled() {
        return settings['prophecy.ui.fabric.creation.enabled'] ?? true;
      },
      get isIDEDataSampleEditEnabled() {
        return settings['prophecy.ui.ide.datasampleedit.enabled'] ?? true;
      },
      get isIDEDataDiffEnabled() {
        return settings['prophecy.ui.ide.datadiff.enabled'] ?? false;
      },
      get isGithubPatDisabled() {
        return settings['prophecy.ui.github.pat.disabled'] === true;
      },
      get getGitProviderOptions() {
        const currentValue = settings['prophecy.ui.git.provider.options'] as string | undefined;
        const options: ExternalGitProvider[] | undefined = parseJSON(currentValue, undefined);
        return options;
      },
      get isIDEUploadDisabled() {
        return settings['prophecy.ui.ide.upload.disabled'] ?? false;
      },
      get isIDEShellDisabled() {
        return settings['prophecy.ui.ide.shell.disabled'] ?? false;
      },
      get isLineageEnabled() {
        return settings['prophecy.ui.lineage.enabled'] ?? true;
      },
      get isMeteringEnabled() {
        return settings['prophecy.ui.metering.enabled'] ?? false;
      },
      get isDatabricksConnectionsEnabled() {
        return Boolean(settings['prophecy.ui.databricks.connections.enabled']);
      },
      get isMigrationThingEnabled() {
        return settings['prophecy.ui.project.new-editor.enabled'] ?? false;
      },
      get isMixPanelEnabled() {
        return settings['prophecy.ui.mixpanel.enabled'] ?? false;
      },
      get isDependencyEnabled() {
        return settings['prophecy.ui.workflow.external.dependency'] ?? false;
      },
      get isDataQualityEnabled() {
        return settings['prophecy.ui.dataquality.expectation.enabled'] ?? true;
      },
      get isTranspilerDataAnalysisEnabled() {
        return settings['prophecy.ui.transpiler.analysis.enabled'] ?? false;
      },
      get hasTranspilerSqlLanguageEnabled() {
        return settings['prophecy.ui.transpiler.sql.enabled'] ?? false;
      },
      get isModeDropdownEnabledInWorkflowCreation() {
        return settings['prophecy.ui.workflow.mode.enabled'] ?? false;
      },
      get isPkgScreenEnabled() {
        return settings['prophecy.ui.package-screen.enabled'] ?? true;
      },
      get isPlaygroundEnabled() {
        return (
          (settings['prophecy.ui.gembuilder.editor.enabled'] ||
            isDebugMode() ||
            localStorage.getItem(ENABLE_GEMBUILDER_EDITOR) === 'true') ??
          false
        );
      },
      get isPythonLanguageEnabled() {
        return settings['prophecy.ui.backend.python.enabled'] ?? false;
      },
      get getExecutionURL() {
        return settings['prophecy.ui.execution.url'] as string;
      },
      get isInterimDownloadAllowed() {
        return settings['prophecy.ui.execution.download.enabled'];
      },
      get isSQLProjectEnabled() {
        return Boolean(settings['prophecy.ui.backend.sql.enabled']);
      },
      get isBetaProvidersEnabled() {
        return (
          Boolean(settings['prophecy.ui.backend.betaproviders.enabled']) ||
          localStorage.getItem(DBT_SHOW_ALL_PROVIDERS) === 'true'
        );
      },
      get isBQOrchestrationEnabled() {
        return (settings['prophecy.ui.backend.bqorchestrator.enabled'] as boolean) ?? false;
      },
      get isPMOrchestrationEnabled() {
        return (settings['prophecy.ui.prophecymanagedwarehouse.enabled'] as boolean) ?? false;
      },
      get isSearchEnabled() {
        return Boolean(settings['prophecy.ui.search.enabled']);
      },
      get isShowCodeInIframeEnabled() {
        return settings['prophecy.ui.gitserver_iframe_enabled'] !== undefined
          ? settings['prophecy.ui.gitserver_iframe_enabled'] === '1'
          : true;
      },
      get isGlobalAccessEnabled() {
        return settings['prophecy.ui.publish.global-access.enabled'] ?? false;
      },
      get isTeamCreationEnabled() {
        return settings['prophecy.ui.team.creation.enabled'] ?? true;
      },
      get isTourEnabled() {
        return settings['prophecy.ui.run.mode'] === 'prod' || false;
      },
      get getUnsetFabricStatusId() {
        return settings.unsetFabricStatusId as number;
      },
      setUnsetFabricStatusId: (fabricStatusId: number) => {
        settings.unsetFabricStatusId = fabricStatusId;
      },
      setFabricStatus: (isSet: boolean) => {
        settings.isFabricSet = isSet;
      },
      get getFabricStatus() {
        return settings.isFabricSet as boolean;
      },
      get getSparkOnboardingStatus() {
        return settings.onboardingStatus as OnboardingStatus;
      },
      setSparkOnboardingStatus: (value: OnboardingStatus) => {
        settings.onboardingStatus = value;
      },
      get getSqlOnboardingStatus() {
        return settings.sqlOnboardingStatus as OnboardingStatus;
      },
      setSqlOnboardingStatus: (value: OnboardingStatus) => {
        settings.sqlOnboardingStatus = value;
      },
      setAnalystProjectId: (value: string) => {
        settings.analystProjectId = value;
      },
      get getAnalystProjectId() {
        return settings.analystProjectId as string | null;
      },
      setUserOnboardingDetails: (value: UserDetails['userOnboardingDetails']) => {
        settings.userOnboardingDetails = value;
      },
      get getUserOnboardingDetails() {
        return settings.userOnboardingDetails as UserDetails['userOnboardingDetails'];
      },
      get isImportEnabled() {
        return settings['prophecy.ui.transpiler.import.enabled'] ?? false;
      },
      get isAlteryxDatasetEnabled() {
        return settings['prophecy.ui.transpiler.alteryx.datasets.enabled'] ?? false;
      },
      get isTranspilerJobEnabled() {
        return settings['prophecy.ui.transpiler.job.enabled'] ?? false;
      },
      get isAdvanceTranspilerEnabled() {
        return settings['prophecy.ui.transpiler.advanceOption.enabled'] ?? false;
      },
      get isWorkflowLanguageEnabled() {
        return settings['prophecy.ui.workflow.language.enabled'] ?? false;
      },
      get isAboutProphecyEnabled() {
        return settings['prophecy.ui.about.enabled'] ?? false;
      },
      get isProjectTemplateEnabled() {
        return settings['prophecy.ui.workflow.build.template.global.enabled'] ?? false;
      },
      get isOnboardingV2Enabled(): boolean {
        return (
          Boolean(settings['prophecy.ui.onboarding.v2.enabled']) ||
          localStorage.getItem('onboardingV2Enabled') === 'true'
        );
      },
      get isSingleForkExtGitConfirmationEnabled() {
        return settings['prophecy.ui.singlefork.externalgit-confirmation.enabled'] ?? false;
      },
      get isCustomSchemaDi() {
        return settings['prophecy.ui.singlefork.externalgit-confirmation.enabled'] ?? false;
      },
      get shouldForceCustomSchemaForScript() {
        return settings['prophecy.ui.script.customSchema.enabled'] ?? false;
      },
      get unitTestEnabledForScript() {
        return settings['prophecy.ui.script.unittest.enabled'] ?? false;
      },
      get isProjectBuildEnable() {
        return localStorage.getItem(PROJECT_BUILD_ENABLED_KEY) === 'true';
      },
      get isDbtMacroEnable() {
        return localStorage.getItem(DBT_MACRO_ENABLED_KEY) === 'true';
      },
      get isDBTProjectDependencyEnabled() {
        return localStorage.getItem(DBT_PROJECT_DEPENDENCY_ENABLED_KEY) === 'true';
      },
      get isGithubEnabled() {
        return settings['prophecy.ui.git.github.enabled'] ?? false;
      },
      get getCloudProvider() {
        return settings['prophecy.ui.cloud.provider'] as string;
      },
      get isNewGembuilderEnabled() {
        return Boolean(settings['prophecy.ui.gembuilder.new.enabled']);
      },
      get isGemDeleteAllowed() {
        return Boolean(settings['prophecy.ui.gembuilder.delete.enabled']);
      },
      get isHistoryEnabled() {
        return settings['prophecy.ui.history.enabled'] || localStorage.getItem(UNDO_REDO_KEY) === 'true';
      },
      get isUploadUsageMetricsEnabled() {
        return settings['prophecy.ui.usageMetrics.enabled'] ?? false;
      },
      /** Do not use this flag, use useIsCopilotEnabled hook instead */
      get isClusterLevelCopilotEnabled() {
        return (
          (settings['prophecy.ui.copilot.enabled'] || localStorage.getItem(COPILOT_ENABLED_KEY) === 'true') &&
          localStorage.getItem(COPILOT_ENABLED_KEY) !== 'false'
        );
      },
      get isReleaseV2Enabled() {
        return settings['prophecy.ui.releasev2.enabled'] as boolean | undefined;
      },
      get isCarryForwardOutputSlugEnabled() {
        return (
          (settings['prophecy.ui.carryForwardSlug.enabled'] as boolean | undefined) ||
          localStorage.getItem(CARRY_FORWARD_SLUG_ENABLED) === 'true'
        );
      },
      get isAirflowEnabled() {
        return Boolean(settings['prophecy.ui.airflow.enabled']);
      },
      get isAirflowTrialEnabled() {
        return Boolean(settings['prophecy.ui.airflow.trial.enabled']);
      },
      get isSynapseEnabled() {
        return Boolean(settings['prophecy.ui.spark.synapse.enabled']);
      },
      get defaultDatabricksAWSMachineType() {
        return (settings['prophecy.ui.databricks.job.size.default.aws.machine'] as string | undefined) || 'i3.xlarge';
      },
      get defaultDatabricksAWSRuntimeEngine() {
        return (
          (settings['prophecy.ui.databricks.job.size.default.aws.runtime_engine'] as string | undefined) || 'PHOTON'
        );
      },
      get isSparkProphecyManagedTimeoutEditable() {
        return localStorage.getItem(SPARK_PROPHECY_MANAGED_TIMEOUT) === 'true';
      },
      get isSparkSynapseExtraJarsEnabled() {
        return settings['prophecy.ui.spark.synapse.extra-jars.enabled'] as boolean | undefined;
      },
      get isSparkDataExplorerEnabled() {
        return (
          (settings['prophecy.ui.editor.workflow.spark.dataexplorer.enabled'] as boolean | undefined) ||
          localStorage.getItem(ENABLE_SPARK_SORT_FILTER) === 'true'
        );
      },
      get getDeleteAfterMergeDefault() {
        return (settings['prophecy.ui.deleteBranchAfterMerge.enabled'] as boolean) ?? false;
      },
      get isCustomDefaultTemplateEnabled() {
        return (
          settings['prophecy.ui.template.defaultTemplate.edit.enabled'] ||
          localStorage.getItem(EDIT_DEFAULT_TEMPLATE_ENABLED_KEY)
        );
      },
      get isSecretManagerEnabled() {
        return settings['prophecy.ui.secretManagement.enabled'] as boolean;
      },
      get isFabricEndpointEnabled() {
        return settings['prophecy.ui.execution.fabric.endpoint.enabled'] as boolean;
      },
      get getDefaultLanguageForProject() {
        return (settings['prophecy.ui.defaultProjectLanguage'] as BackEndCodeLanguage) || BackEndCodeLanguage.python;
      },
      get getIsFabricAccessibleToClusterAdmin() {
        return (
          (settings['prophecy.ui.allow.fabric.access.cluster.admins'] as boolean) ||
          localStorage.getItem(ALLOW_FABRIC_ACCESS_CLUSTER_ADMIN) === 'true'
        );
      },
      get fabricConnectionCreationRole() {
        return settings['prophecy.ui.allow.fabric.connection.creation.role'] as
          | FabricConnectionCreationRole
          | undefined;
      },
      get showConfigTagsForTesting() {
        return (
          localStorage.getItem(ALLOW_CONFIG_TAGS) === 'true' &&
          window.location.pathname.startsWith(`${BASE_PATH}/ide/pipeline`)
        );
      },
      get isSecretTypeConfigEnabled() {
        return localStorage.getItem(ALLOW_SECRET_TYPE_CONFIG)
          ? localStorage.getItem(ALLOW_SECRET_TYPE_CONFIG) === 'true'
          : true;
      },

      get isParamConfigEnabled() {
        return localStorage.getItem(ALLOW_PARAM_CONFIG) === 'true';
      },
      get isSparkOnDemandCompilationEnabled() {
        // NOTE: we need to keep the old key for backward compatibility. the old flag is deprecated and will be removed in the future.
        return Boolean(
          settings['prophecy.ui.editor.workflow.spark.on_demand_compilation'] ||
          settings['prophecy.ui.editor.on_demand_compilation'] ||
          false
        );
      },
      get isSqlOnDemandCompilationEnabled() {
        // for sql don't rely on the old key
        return Boolean(settings['prophecy.ui.editor.on_demand_compilation'] || false);
      },

      get isDatabricksOIDCEnabled() {
        return (settings['prophecy.ui.databricks.oidc.enabled'] as boolean) ?? false;
      },
      get isCopilotAudioPromptEnabled() {
        return (
          (settings['prophecy.ui.copilot.enable_audio'] as boolean) ||
          localStorage.getItem(COPILOT_AUDIO_PROMPT_ENABLED) === 'true'
        );
      },
      get isIncrementalCodegenEnabled(): boolean {
        return (settings['prophecy.ui.spark.incremental.submission.enabled'] as boolean) ?? false;
      },
      get isDataProfileFFEnabled() {
        return localStorage.getItem(ALLOW_DATA_PROFILE_ON_INTERIMS) === 'true';
      },
      get isZstdCompressionDisabled() {
        return localStorage.getItem(DISABLE_ZSTD_COMPRESSION) === 'true';
      },
      get oAuthContinousLoginCheckEnabled(): boolean {
        return (settings['prophecy.ui.oauth.continuous-login-check.enabled'] as boolean) ?? false;
      },
      get oAuthContinousLoginCheckFrequency(): number {
        return Number(settings['prophecy.ui.oauth.continuous-login-check.frequency.minutes']);
      },
      get isWebsocketDebugEnabled(): boolean {
        return (settings['prophecy.ui.debugWebsocket.enabled'] as boolean) ?? false;
      },
      get isAnalyzerEnabledForInformatica() {
        return localStorage.getItem(ENABLE_ANALYZER_FOR_INFORMATICA) === 'true';
      },
      get isCopilotKinesisV2ChatEnabled() {
        return (settings['prophecy.ui.copilot.kinesis_enabled'] as boolean) ?? false;
      },
      get isCopilotKinesisChatFeedbackEnabled() {
        return (settings['prophecy.ui.copilot.kinesis_feedback_enabled'] as boolean) ?? false;
      },
      get isInternalCopilotKinesisChatFeedbackEnabled() {
        return (settings['prophecy.ui.copilot.internal_kinesis_feedback_enabled'] as boolean) ?? false;
      },
      get showJpmcPostLoginCopilotPopup() {
        return (settings['prophecy.ui.show_post_login_copilot_popup'] as boolean) ?? false;
      },

      get isGoogleOem() {
        return (
          (settings['prophecy.ui.google.oem.enabled'] as boolean) || localStorage.getItem(ENABLE_GOOGLE_OEM) === 'true'
        );
      },

      get isSapEnabled() {
        return (settings['prophecy.ui.sap.enabled'] as boolean) || localStorage.getItem(ENABLE_SAP) === 'true';
      },
      get isOAuthFabricTestingEnabled() {
        return localStorage.getItem(ENABLE_OAUTH_FABRIC_TESTING) === 'true';
      },
      get isReleaseDiagnosticsEnabled() {
        return (settings['prophecy.ui.release.diagnostics.enabled'] as boolean) ?? false;
      },
      get isCreditsEnabled() {
        return (settings['prophecy.ui.credits.enabled'] as boolean) ?? false;
      },
      get awsSamlLoginUrls() {
        const appLoginUrls = settings['prophecy.ui.aws.saml.appLoginUrls'] as string;

        if (!appLoginUrls) {
          return [];
        }

        return appLoginUrls.split(',');
      },
      get awsSamlAssumedRoles() {
        const assumedRoles = settings['prophecy.ui.aws.saml.assumedRoles'] as string;

        if (!assumedRoles) {
          return [];
        }

        return assumedRoles.split(',');
      },
      get isProphecyAutomateDisabled() {
        return (settings['prophecy.ui.automate.disabled'] as boolean) ?? false;
      },
      get defaultInputDebounceInMs() {
        return Number(settings['prophecy.ui.defaultInputDebounceInMs'] ?? 250);
      },
      get isOrchestratorDebugModeEnabled() {
        return localStorage.getItem(ENABLE_ORCHESTRATOR_DEBUG_MODE) === 'true';
      },
      get isProfessionalEditionEnabled(): boolean {
        return (
          (settings['prophecy.ui.professional.edition.enabled'] as boolean) ||
          localStorage.getItem(ENABLE_PROFESSIONAL_EDITION) === 'true'
        );
      },
      get isShareAndReplaysEnabled(): boolean {
        return localStorage.getItem(ENABLE_SHARE_AND_REPLAYS_ENABLED) === 'true';
      },
      get isRepoEnabledInDependency() {
        return (
          (settings['prophecy.ui.config.pypiDependency.repo.enabled'] as boolean) ||
          localStorage.getItem(IS_REPO_ENABLED_IN_DEPENDENCY) === 'true'
        );
      },
      get isTranspilerIicsEnabled() {
        return localStorage.getItem(ENABLE_TRANSPILER_IICS) === 'true';
      },
      get isAutoSwitchInChatModeDisabled() {
        return localStorage.getItem(DISABLE_AUTO_SWITCH_IN_CHAT_MODE) === 'true';
      },
      get isDataHarmonizationEnabled() {
        return (
          (settings['prophecy.ui.data_harmonization.enabled'] as boolean) ||
          localStorage.getItem(ENABLE_DATA_HARMONIZATION) === 'true'
        );
      },
      get isTableDataQualityEnabled() {
        return true;
      },
      get isSQLPythonBackendEnabled() {
        return (settings['prophecy.ui.automate.backend.python.enabled'] as boolean) ?? false;
      },
      get isPipelineInJobEnabled() {
        return (settings['prophecy.ui.automate.pipelineInJob.enabled'] as boolean) ?? false;
      },
      get isUnifiedFabricEnabled() {
        return (settings['prophecy.ui.unified.fabric.enabled'] as boolean) ?? false;
      },
      get isCopyPasteInDifferentProvidersEnabled() {
        return localStorage.getItem(ENABLE_COPY_PASTE_IN_DIFFERENT_PROVIDERS) === 'true';
      },
      get isConnectionMonitoringEnabled() {
        return (settings['prophecy.ui.connection.monitoring.enabled'] as boolean) ?? false;
      },
      get isOrchAirflowConnectionsEnabled() {
        return (settings['prophecy.ui.orchestration.airflow_connections_enabled'] as boolean) ?? false;
      },
      get isFeatureManagementAvailable() {
        return (settings[FEATURE_MANAGEMENT_ENABLED_KEY] as boolean) ?? false;
      },
      get showOnlyAirflowConnections() {
        return (settings['prophecy.ui.automate.airflowConnectionsOnly'] as boolean) ?? false;
      },
      get isHistoricalRunSnapshotsEnabled() {
        return (
          (settings['prophecy.ui.runtimelogs.enabled'] as boolean) ||
          localStorage.getItem(ENABLE_HISTORICAL_RUN_SNAPSHOTS) === 'true'
        );
      },
      get isAgentV4EnabledAtClusterLevel() {
        return (settings['prophecy.ui.agent.v4.enabled.cluster'] as boolean) ?? false;
      },
      get isSqlLineageEnabled() {
        return (
          (settings['prophecy.ui.sql.lineage.enabled'] as boolean) ||
          localStorage.getItem(ENABLE_SQL_LINEAGE) === 'true'
        );
      },
      get dateConfigUIFormat() {
        return (settings['prophecy.ui.spark.pyspark.dateConfigUIFormat'] as string) || '';
      },
      get isOrchestrationAiDataAccessEnabled() {
        return (
          (this.isAgentV4EnabledAtClusterLevel &&
            (settings['prophecy.ui.ai.data.access.cluster.enabled'] as boolean)) ??
          false
        );
      },
      get isExpensiveCssDisabled() {
        return getIsExpensiveCssDisabled(settings);
      }
    }),
    [settings]
  );
};

export type SecuredGlobalConfigType = ReturnType<typeof useGlobalVariables>;
